import { styled } from "@mui/material/styles";
import { Box, Typography, Card, Avatar } from "@mui/material";
import { colors, fonts } from "../../../theme/theme";

export const RootBox = styled(Box)(({ theme }) => ({
  maxWidth: 900,
  marginLeft: "auto",
  marginRight: "auto",
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

export const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
  flexWrap: "wrap",
}));

export const PageHeading = styled(Typography)({
  fontFamily: fonts.gotham,
  fontWeight: 600,
  fontSize: 28,
  color: colors.primary,
});

export const PageSubheading = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 15,
  color: colors.textLight,
  marginTop: 4,
});

export const LoadingBox = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: "50vh",
});

export const SectionCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  borderRadius: 16,
  boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
  // CustomInput/CustomTextArea default their inner input to width 75% (on the
  // .MuiOutlinedInput-root). Force every field to fill its grid cell so all
  // inputs line up at an equal, full width.
  "& .MuiOutlinedInput-root": { width: "100% !important" },
  "& .MuiInputBase-root": { width: "100%" },
  // CustomDropdown wraps its FormControl with marginTop:6px, which pushes the
  // Gender field down out of alignment with the text inputs. Zero it out.
  "& .MuiFormControl-root": { marginTop: 0 },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

export const SectionTitle = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  marginBottom: theme.spacing(2.5),
  "& svg": { color: colors.secondary },
  fontFamily: fonts.gotham,
  fontWeight: 600,
  fontSize: 18,
  color: colors.primary,
}));

// Two-column responsive field grid.
export const FieldGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

// A cell spanning both columns (bio, textareas, tag inputs).
export const FullRow = styled(Box)({
  gridColumn: "1 / -1",
});

export const AvatarRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

export const ProfileAvatar = styled(Avatar)({
  width: 64,
  height: 64,
  backgroundColor: colors.secondary,
  color: colors.white,
  fontFamily: fonts.gotham,
  fontSize: 22,
});

export const AvatarName = styled(Typography)({
  fontFamily: fonts.gotham,
  fontWeight: 600,
  fontSize: 18,
  color: colors.primary,
});

export const AvatarSubtitle = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 14,
  color: colors.textLight,
});

export const CheckboxRow = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 4,
  fontFamily: fonts.quicksand,
  color: colors.text,
});

export const StatusPill = styled(Box)(({ $approved }) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 12px",
  borderRadius: 20,
  fontFamily: fonts.quicksand,
  fontSize: 13,
  fontWeight: 600,
  backgroundColor: $approved ? "#E7F4E8" : "#FDF2E2",
  color: $approved ? colors.success : colors.accent,
}));

export const FieldLabel = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: colors.textLight,
  marginBottom: 6,
});

// Matches the built-in label of CustomInput/CustomDropdown so the phone field
// lines up with the rest of the form.
export const InputFieldLabel = styled(Typography)({
  fontFamily: "'Quicksand', sans-serif",
  fontSize: "1rem",
  fontWeight: 600,
  color: "#33261D",
  marginBottom: 8,
});

// react-phone-input-2 inline styles that mimic CustomInput (no border, soft
// shadow, white rounded 55px, Quicksand).
export const phoneInputStyle = {
  width: "100%",
  height: "55px",
  background: "#fff",
  border: "none",
  borderRadius: "16px",
  boxShadow:
    "-4px -4px 17.8px 0px rgba(205, 214, 223, 0.15), 4px 4px 15px 0px rgba(205, 214, 223, 0.25)",
  fontFamily: "'Quicksand', sans-serif",
  fontSize: "16px",
  color: "#33261D",
  paddingLeft: "52px",
};
export const phoneButtonStyle = {
  border: "none",
  background: "transparent",
  borderRadius: "16px 0 0 16px",
  paddingLeft: "8px",
};

export const ComplianceRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: theme.spacing(1.5, 0),
  borderBottom: `1px solid ${colors.headerBg}`,
  "&:last-of-type": { borderBottom: "none" },
}));

export const ComplianceLabel = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: 8,
  fontFamily: fonts.quicksand,
  fontSize: 15,
  color: colors.text,
});

export const SaveBar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "flex-end",
  marginTop: theme.spacing(2),
}));

// Banner shown above the form when the practitioner has an unpublished submission.
export const ReviewBanner = styled(Box, {
  shouldForwardProp: (p) => p !== "$rejected",
})(({ theme, $rejected }) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1.5),
  padding: theme.spacing(2, 2.5),
  borderRadius: 14,
  marginBottom: theme.spacing(3),
  fontFamily: fonts.quicksand,
  border: `1px solid ${$rejected ? "#F0C2C2" : "#E7D9BE"}`,
  backgroundColor: $rejected ? "#FBE7E7" : "#FDF6E7",
  "& svg": { color: $rejected ? "#C0392B" : "#C77A22", marginTop: 2 },
}));

export const ReviewBannerTitle = styled(Typography)({
  fontFamily: fonts.gotham,
  fontWeight: 600,
  fontSize: 15,
  color: colors.primary,
});

export const ReviewBannerText = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 14,
  color: colors.text,
  marginTop: 2,
});

// Avatar with a camera/upload affordance.
export const AvatarWrap = styled(Box)({
  position: "relative",
  width: 64,
  height: 64,
});

export const AvatarEditButton = styled("button")({
  position: "absolute",
  right: -2,
  bottom: -2,
  width: 24,
  height: 24,
  borderRadius: "50%",
  border: `2px solid ${colors.white}`,
  backgroundColor: colors.secondary,
  color: colors.white,
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  "& svg": { fontSize: 14 },
});

// One compliance-document row: label + status + upload control + uploaded files.
export const DocRow = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  borderBottom: `1px solid ${colors.headerBg}`,
  "&:last-of-type": { borderBottom: "none" },
}));

export const DocHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 8,
  flexWrap: "wrap",
});

export const DocControls = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(1),
}));

export const HelperText = styled(Typography)({
  fontFamily: fonts.quicksand,
  fontSize: 12,
  color: colors.textLight,
});
